import * as React from 'react';
import {
    Select,
    Button,
    Tag,
    Space,
    Tooltip,
    TimePicker,
    InputNumber,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
    },
    dockFormRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-end',
    },
    fieldContainer: {
        flex: 1,
    },
    fieldLabel: {
        color: '#333333',
        fontSize: 12,
        marginBottom: 4,
        display: 'block',
    },
    mappingCard: {
        border: '1px solid #e8e8e8',
        borderRadius: '8px',
        padding: '12px',
        marginTop: '8px',
        backgroundColor: '#fafafa',
    },
    mappingHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
    },
    dockTitle: {
        fontWeight: 600,
        fontSize: '14px',
    },
    vehicleTags: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '4px',
    },
    actionButtons: {
        display: 'flex',
        gap: '4px',
    },
    cardMeta: {
        marginTop: '6px',
        fontSize: '12px',
        color: '#666',
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap' as const,
    },
};

export interface DockMappingEntry {
    vehicleMakes: string[];
    startTime?: string;
    endTime?: string;
    recycleTimeMinutes?: number;
}

export type DockMappingValue = Record<string, DockMappingEntry>;

export interface DockEntry {
    dockNumber: string;
    vehicleMakes: string[];
    operatingHours?: [[string, string]];
    recycleTimeMinutes?: number;
}

export const dockMappingToApiArray = (mapping: DockMappingValue): DockEntry[] =>
    // eslint-disable-next-line implicit-arrow-linebreak
    Object.entries(mapping).map(([dockNumber, entry]) => ({
        dockNumber,
        vehicleMakes: entry.vehicleMakes,
        ...(entry.startTime && entry.endTime
            ? { operatingHours: [[entry.startTime, entry.endTime]] as [[string, string]] }
            : {}),
        ...(entry.recycleTimeMinutes ? { recycleTimeMinutes: entry.recycleTimeMinutes } : {}),
    }));

export const dockMappingFromApiArray = (entries: DockEntry[]): DockMappingValue => {
    if (!Array.isArray(entries)) return {};
    return entries.reduce(
        (acc, entry) => ({
            ...acc,
            [entry.dockNumber]: {
                vehicleMakes: entry.vehicleMakes || [],
                startTime: entry.operatingHours?.[0]?.[0],
                endTime: entry.operatingHours?.[0]?.[1],
                recycleTimeMinutes: entry.recycleTimeMinutes,
            },
        }),
        {} as DockMappingValue,
    );
};

interface DockVehicleMappingProps {
    dockCapacity: number;
    value?: DockMappingValue;
    onChange?: (value: DockMappingValue) => void;
    vehicleMakeOptions: Array<{ value: string; label: string }>;
    disabled?: boolean;
}

const TIME_FORMAT = 'HH:mm';

interface DockFormState {
    dock: string | null;
    vehicles: string[];
    start: string | null;
    end: string | null;
    recycleTime: number | null;
    editingDock: string | null;
}

const INITIAL_DOCK_FORM: DockFormState = {
    dock: null,
    vehicles: [],
    start: null,
    end: null,
    recycleTime: null,
    editingDock: null,
};

const DockVehicleMapping: React.FC<DockVehicleMappingProps> = ({
    dockCapacity,
    value: valueProp,
    onChange,
    vehicleMakeOptions,
    disabled = false,
}) => {
    const { t } = useTranslation();
    // Form.Item can inject `null` (e.g. existing consignment edit with no mapping).
    // A default param only guards `undefined`, so normalise here to avoid Object.keys(null).
    const value = valueProp || {};
    const [dockForm, setDockForm] = React.useState<DockFormState>(INITIAL_DOCK_FORM);

    const isDisabled = disabled || !dockCapacity || dockCapacity <= 0;

    const dockOptions = React.useMemo(() => {
        if (!dockCapacity || dockCapacity <= 0) return [];
        return Array.from({ length: dockCapacity }, (_, i) => ({
            value: String(i + 1),
            label: `Dock ${i + 1}`,
        }));
    }, [dockCapacity]);

    const availableDocks = React.useMemo(() => {
        return dockOptions.filter(
            (d) => !Object.keys(value).includes(d.value) || d.value === dockForm.editingDock,
        );
    }, [dockOptions, value, dockForm.editingDock]);

    React.useEffect(() => {
        if (dockCapacity && value && onChange) {
            const newValue = { ...value };
            let hasChanges = false;
            Object.keys(newValue).forEach((dockNum) => {
                if (parseInt(dockNum, 10) > dockCapacity) {
                    delete newValue[dockNum];
                    hasChanges = true;
                }
            });
            if (hasChanges) {
                onChange(newValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dockCapacity, onChange]);

    const isTimeRangeInvalid = !!(
        (dockForm.start && !dockForm.end)
        || (!dockForm.start && dockForm.end)
        || (dockForm.start && dockForm.end && dockForm.start >= dockForm.end)
    );

    const handleCreateMapping = () => {
        if (!dockForm.dock || !onChange || isTimeRangeInvalid) return;
        const entry: DockMappingEntry = {
            vehicleMakes: dockForm.vehicles,
            ...(dockForm.start ? { startTime: dockForm.start } : {}),
            ...(dockForm.end ? { endTime: dockForm.end } : {}),
            ...(dockForm.recycleTime ? { recycleTimeMinutes: dockForm.recycleTime } : {}),
        };
        // Preserve insertion order: when editing, replace in-place; when creating, append at the end
        if (dockForm.editingDock) {
            onChange(
                Object.fromEntries(
                    Object.entries(value).map(([dock, existingEntry]) => {
                        return dock === dockForm.editingDock ? [dock, entry] : [dock, existingEntry];
                    }),
                ),
            );
        } else {
            onChange({ ...value, [dockForm.dock]: entry });
        }
        setDockForm(INITIAL_DOCK_FORM);
    };

    const handleDeleteMapping = (dock: string) => {
        if (!onChange) return;
        const newValue = { ...value };
        delete newValue[dock];
        onChange(newValue);
    };

    const handleEditMapping = (dock: string) => {
        setDockForm({
            dock,
            editingDock: dock,
            vehicles: value[dock]?.vehicleMakes || [],
            start: value[dock]?.startTime || null,
            end: value[dock]?.endTime || null,
            recycleTime: value[dock]?.recycleTimeMinutes || null,
        });
    };

    const sortedMappings = Object.keys(value)
        .map(Number)
        .sort((a, b) => a - b)
        .map(String);

    return (
        <div style={styles.container}>
            {/* Row 1: Dock number + Vehicle makes */}
            <div style={styles.dockFormRow}>
                <div style={styles.fieldContainer}>
                    <span style={styles.fieldLabel}>{t('dockNumber')}</span>
                    <Select
                        value={dockForm.dock}
                        onChange={(dock) => setDockForm((s) => ({ ...s, dock }))}
                        options={availableDocks}
                        placeholder={t('selectDockNumber')}
                        disabled={isDisabled}
                        style={{ width: '100%' }}
                        allowClear
                    />
                </div>
                <div style={styles.fieldContainer}>
                    <span style={styles.fieldLabel}>{t('vehicleMakes')}</span>
                    <Select
                        mode="multiple"
                        value={dockForm.vehicles}
                        onChange={(vehicles) => setDockForm((s) => ({ ...s, vehicles }))}
                        options={vehicleMakeOptions}
                        placeholder={t('selectVehicleMakes')}
                        disabled={isDisabled}
                        style={{ width: '100%' }}
                        maxTagCount="responsive"
                    />
                </div>
            </div>

            {/* Row 2: Operating hours + Recycle time */}
            <div style={styles.dockFormRow}>
                <div style={styles.fieldContainer}>
                    <span style={styles.fieldLabel}>{t('startTime')}</span>
                    <TimePicker
                        value={dockForm.start ? moment(dockForm.start, TIME_FORMAT) : null}
                        onChange={(_, timeStr) => {
                            setDockForm((s) => ({ ...s, start: (timeStr as string) || null }));
                        }}
                        format={TIME_FORMAT}
                        placeholder={t('startTime')}
                        disabled={isDisabled}
                        style={{ width: '100%' }}
                        status={isTimeRangeInvalid ? 'error' : undefined}
                        allowClear
                    />
                </div>
                <div style={styles.fieldContainer}>
                    <span style={styles.fieldLabel}>{t('endTime')}</span>
                    <TimePicker
                        value={dockForm.end ? moment(dockForm.end, TIME_FORMAT) : null}
                        onChange={(_, timeStr) => {
                            setDockForm((s) => ({ ...s, end: (timeStr as string) || null }));
                        }}
                        format={TIME_FORMAT}
                        placeholder={t('endTime')}
                        disabled={isDisabled}
                        style={{ width: '100%' }}
                        status={isTimeRangeInvalid ? 'error' : undefined}
                        allowClear
                    />
                    {isTimeRangeInvalid && (
                        <span style={{ color: '#ff4d4f', fontSize: 12 }}>
                            {t('endTimeMustBeAfterStartTime')}
                        </span>
                    )}
                </div>
                <div style={styles.fieldContainer}>
                    <span style={styles.fieldLabel}>{t('recycleTimeMinutes')}</span>
                    <InputNumber
                        value={dockForm.recycleTime}
                        onChange={(recycleTime) => setDockForm((s) => ({ ...s, recycleTime }))}
                        min={1}
                        precision={0}
                        addonAfter="min"
                        placeholder="0"
                        disabled={isDisabled}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>

            {/* Action Buttons */}
            <Space>
                <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={handleCreateMapping}
                    disabled={isDisabled || !dockForm.dock || isTimeRangeInvalid}
                >
                    {dockForm.editingDock ? t('updateMapping') : t('createMapping')}
                </Button>
                {dockForm.editingDock && (
                    <Button type="link" onClick={() => setDockForm(INITIAL_DOCK_FORM)}>
                        {t('cancel')}
                    </Button>
                )}
            </Space>

            {/* Existing Mappings */}
            {sortedMappings.map((dock) => (
                <div key={dock} style={styles.mappingCard}>
                    <div style={styles.mappingHeader}>
                        <span style={styles.dockTitle}>{`Dock ${dock}`}</span>
                        <div style={styles.actionButtons}>
                            <Tooltip title={t('edit')}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditMapping(dock)}
                                    disabled={disabled}
                                />
                            </Tooltip>
                            <Tooltip title={t('delete')}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteMapping(dock)}
                                    disabled={disabled}
                                    danger
                                />
                            </Tooltip>
                        </div>
                    </div>
                    <div style={styles.vehicleTags}>
                        {value[dock]?.vehicleMakes?.map((vehicle) => (
                            <Tag key={vehicle} color="blue">
                                {vehicle}
                            </Tag>
                        ))}
                    </div>
                    {(value[dock]?.startTime || value[dock]?.endTime || value[dock]?.recycleTimeMinutes) && (
                        <div style={styles.cardMeta}>
                            {(value[dock]?.startTime || value[dock]?.endTime) && (
                                <span>
                                    {`${t('operatingHours')}: `}
                                    {value[dock].startTime}
                                    {' – '}
                                    {value[dock].endTime}
                                </span>
                            )}
                            {value[dock]?.recycleTimeMinutes && (
                                <span>
                                    {`${t('recycleTimeMinutes')}: ${value[dock].recycleTimeMinutes} min`}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default DockVehicleMapping;
